import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import Layout from '../../../components/Layout';
import { knowledgeBaseAPI } from '../../../api';
import { useAuthStore } from '../../../store/authStore';
import { useToast } from '../../../hooks/useToast';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { FormLabel } from '../../../components/ui/FormLabel';
import { cn } from '../../../lib/utils';
import {
  KB_CATEGORY_OPTIONS,
  KB_MANUFACTURER_INDEX_CATEGORIES,
  KB_MANUFACTURER_REQUIRED_CATEGORIES,
  kbCategoryLabel,
} from '../../../lib/knowledgeBaseCategories';
import {
  BookOpen,
  Download,
  Loader2,
  Search,
  Trash2,
  Upload,
  Library,
  FolderUp,
  Tag,
  X,
  History,
  FileUp,
} from 'lucide-react';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

function normalizeTagSlugClient(raw) {
  if (raw == null) return '';
  let s = String(raw).trim().toLowerCase();
  s = s.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s.slice(0, 120);
}

const UPLOAD_DISCLAIMER = (
  <>
    <p className="font-semibold text-[var(--ds-text-primary)] mb-2">Knowledge base uploads</p>
    <p className="text-sm text-[var(--ds-text-secondary)] leading-relaxed">
      Upload only technical manuals, standards excerpts you are permitted to share, training materials, and
      other <strong>non-sensitive</strong> reference documents. <strong>Do not upload</strong> personal
      data, customer or job details, financial records, passwords, private correspondence, or any{' '}
      <strong>confidential</strong> information. You are responsible for compliance with copyright and
      licensing. SparxMate may remove content that violates policy or poses a security risk.
    </p>
  </>
);

function formatBytes(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

export default function KnowledgeBase() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenantId;
  const { success, error: toastError, ToastContainer } = useToast();

  const [tab, setTab] = useState('browse');

  const [page, setPage] = useState(1);
  const limit = 20;
  const [categoryFilter, setCategoryFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [sort, setSort] = useState('created_at_desc');
  const [manufacturerPrefix, setManufacturerPrefix] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [tagEditDoc, setTagEditDoc] = useState(null);
  const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);
  const [replaceFileDoc, setReplaceFileDoc] = useState(null);
  const [versionDlKey, setVersionDlKey] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, visibilityFilter, sort, manufacturerPrefix, debouncedQ, filterTags]);

  const showManufacturerRail =
    categoryFilter && KB_MANUFACTURER_INDEX_CATEGORIES.has(categoryFilter);

  const listQuery = useQuery({
    queryKey: [
      'knowledge-base',
      'documents',
      {
        page,
        limit,
        categoryFilter,
        visibilityFilter,
        sort,
        manufacturerPrefix,
        debouncedQ,
        filterTags,
      },
    ],
    queryFn: () =>
      knowledgeBaseAPI.listDocuments({
        page,
        limit,
        category: categoryFilter || undefined,
        visibility: visibilityFilter,
        sort,
        manufacturer_prefix: manufacturerPrefix || undefined,
        q: debouncedQ || undefined,
        tags: filterTags.length ? filterTags.join(',') : undefined,
      }),
    keepPreviousData: true,
  });

  const payload = listQuery.data?.data;
  const items = payload?.items ?? [];
  const total = payload?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const canModifyDoc = useCallback(
    (doc) => {
      if (!doc) return false;
      if (user?.role === 'global_admin') return true;
      return doc.ownerTenantId === tenantId;
    },
    [user?.role, tenantId]
  );

  const [downloadingId, setDownloadingId] = useState(null);

  const downloadDoc = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const res = await knowledgeBaseAPI.downloadDocument(doc.id);
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalFileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toastError('Download failed', e?.message || 'Could not download file');
    } finally {
      setDownloadingId(null);
    }
  };

  const [deletingId, setDeletingId] = useState(null);

  const deleteMut = useMutation({
    mutationFn: (id) => knowledgeBaseAPI.deleteDocument(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['knowledge-base', 'documents']);
      success('Removed', 'Document was removed from the knowledge base.');
    },
    onError: (e) => toastError('Remove failed', e?.message || 'Could not remove document'),
  });

  const handleDelete = (doc) => {
    if (!canModifyDoc(doc)) return;
    if (!window.confirm(`Remove “${doc.title}” from the knowledge base?`)) return;
    setDeletingId(doc.id);
    deleteMut.mutate(doc.id, {
      onSettled: () => setDeletingId(null),
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <ToastContainer />
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-display font-bold text-[var(--ds-text-primary)] flex items-center gap-2">
              <BookOpen className="h-8 w-8 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
              Knowledge base
            </h1>
            <p className="text-meta text-[var(--ds-text-secondary)] mt-1 max-w-2xl">
              Shared reference documents across SparxMate tenants. Public files are visible to all
              businesses; private files are only visible to your company.
            </p>
          </div>
          <div className="flex rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-1 bg-[var(--ds-surface-panel)] shrink-0">
            <Button
              type="button"
              variant={tab === 'browse' ? 'default' : 'ghost'}
              size="sm"
              className="gap-2"
              onClick={() => setTab('browse')}
            >
              <Library className="h-4 w-4" />
              Browse
            </Button>
            <Button
              type="button"
              variant={tab === 'upload' ? 'default' : 'ghost'}
              size="sm"
              className="gap-2"
              onClick={() => setTab('upload')}
            >
              <FolderUp className="h-4 w-4" />
              Upload
            </Button>
          </div>
        </div>

        {tab === 'browse' && (
          <BrowsePanel
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            visibilityFilter={visibilityFilter}
            setVisibilityFilter={setVisibilityFilter}
            sort={sort}
            setSort={setSort}
            manufacturerPrefix={manufacturerPrefix}
            setManufacturerPrefix={setManufacturerPrefix}
            showManufacturerRail={showManufacturerRail}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            filterTags={filterTags}
            setFilterTags={setFilterTags}
            listQuery={listQuery}
            items={items}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            total={total}
            tenantId={tenantId}
            onDownload={(doc) => downloadDoc(doc)}
            downloadBusyId={downloadingId}
            onDelete={handleDelete}
            deleteBusyId={deletingId}
            canModifyDoc={canModifyDoc}
            onEditTags={setTagEditDoc}
            onVersionHistory={setVersionHistoryDoc}
            onReplaceFile={setReplaceFileDoc}
          />
        )}

        {tab === 'upload' && (
          <UploadPanel
            onUploaded={() => {
              queryClient.invalidateQueries(['knowledge-base', 'documents']);
              setTab('browse');
              success('Uploaded', 'Your document is now in the knowledge base.');
            }}
            onError={(msg) => toastError('Upload failed', msg)}
          />
        )}

        {tagEditDoc && (
          <EditDocumentTagsModal
            doc={tagEditDoc}
            onClose={() => setTagEditDoc(null)}
            onSaved={() => {
              queryClient.invalidateQueries(['knowledge-base', 'documents']);
              setTagEditDoc(null);
              success('Tags saved', 'Document tags were updated.');
            }}
            onError={(msg) => toastError('Could not save tags', msg)}
          />
        )}

        {versionHistoryDoc && (
          <VersionHistoryModal
            doc={versionHistoryDoc}
            onClose={() => setVersionHistoryDoc(null)}
            onError={(msg) => toastError('Versions', msg)}
            downloadBusyKey={versionDlKey}
            setDownloadBusyKey={setVersionDlKey}
          />
        )}

        {replaceFileDoc && (
          <ReplaceFileModal
            doc={replaceFileDoc}
            onClose={() => setReplaceFileDoc(null)}
            onDone={() => {
              queryClient.invalidateQueries(['knowledge-base', 'documents']);
              queryClient.invalidateQueries(['knowledge-base', 'versions']);
              setReplaceFileDoc(null);
              success('File replaced', 'A new version was saved; previous files are kept in history.');
            }}
            onError={(msg) => toastError('Replace failed', msg)}
          />
        )}
      </div>
    </Layout>
  );
}

function BrowsePanel({
  categoryFilter,
  setCategoryFilter,
  visibilityFilter,
  setVisibilityFilter,
  sort,
  setSort,
  manufacturerPrefix,
  setManufacturerPrefix,
  showManufacturerRail,
  searchInput,
  setSearchInput,
  filterTags,
  setFilterTags,
  listQuery,
  items,
  page,
  setPage,
  totalPages,
  total,
  tenantId,
  onDownload,
  downloadBusyId,
  onDelete,
  deleteBusyId,
  canModifyDoc,
  onEditTags,
  onVersionHistory,
  onReplaceFile,
}) {
  const [tagFilterInput, setTagFilterInput] = useState('');
  const [tagFilterSuggestions, setTagFilterSuggestions] = useState([]);

  useEffect(() => {
    const q = tagFilterInput.trim();
    if (q.length < 1) {
      setTagFilterSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      knowledgeBaseAPI
        .tagSuggestions({ q, limit: 20 })
        .then((res) => setTagFilterSuggestions(res?.data?.items ?? []))
        .catch(() => setTagFilterSuggestions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [tagFilterInput]);

  const addFilterTag = (raw) => {
    const slug = normalizeTagSlugClient(raw);
    if (!slug || filterTags.includes(slug)) return;
    setFilterTags([...filterTags, slug]);
    setTagFilterInput('');
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ds-text-muted)]" />
              <Input
                className="pl-9"
                placeholder="Search title, description, manufacturer…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search knowledge base"
              />
            </div>
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              {KB_CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Select
              label="Visibility"
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
            >
              <option value="all">All public and my private</option>
              <option value="public">Public only</option>
              <option value="private">Private only</option>
              <option value="mine">My uploads</option>
            </Select>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="created_at_desc">Newest first</option>
              <option value="title_asc">Title A–Z</option>
              <option value="manufacturer_asc">Manufacturer A–Z</option>
            </Select>
          </div>

          <div>
            <p className="text-micro font-medium text-[var(--ds-text-secondary)] mb-2 flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" aria-hidden />
              Tags (match all)
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {filterTags.length === 0 ? (
                <span className="text-xs text-[var(--ds-text-muted)]">No tag filter — show all matching documents</span>
              ) : (
                filterTags.map((slug) => (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-[var(--ds-accent-primary)]/15 text-[var(--ds-text-primary)] border border-[var(--ds-border-subtle)]"
                  >
                    {slug}
                    <button
                      type="button"
                      className="p-0.5 rounded-full hover:bg-[var(--ds-surface-panel)]"
                      onClick={() => setFilterTags(filterTags.filter((s) => s !== slug))}
                      aria-label={`Remove tag ${slug}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="relative max-w-md">
              <Input
                className="pr-10"
                placeholder="Type a tag and press Enter…"
                value={tagFilterInput}
                onChange={(e) => setTagFilterInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFilterTag(tagFilterInput);
                  }
                }}
                list="kb-tag-filter-suggestions"
                aria-label="Add tag filter"
              />
              <datalist id="kb-tag-filter-suggestions">
                {tagFilterSuggestions.map((item) => (
                  <option key={item.slug} value={item.slug}>
                    {item.label || item.slug}
                  </option>
                ))}
              </datalist>
            </div>
          </div>

          {showManufacturerRail && (
            <div>
              <p className="text-micro font-medium text-[var(--ds-text-secondary)] mb-2">
                Manufacturer starts with
              </p>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setManufacturerPrefix('')}
                  className={cn(
                    'px-2 py-1 text-xs rounded-[var(--ds-radius-md)] border transition-colors',
                    !manufacturerPrefix
                      ? 'bg-[var(--ds-accent-primary)] text-white border-transparent'
                      : 'border-[var(--ds-border-subtle)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]'
                  )}
                >
                  Any
                </button>
                {LETTERS.map((L) => {
                  const lower = L.toLowerCase();
                  const active = manufacturerPrefix === lower;
                  return (
                    <button
                      key={L}
                      type="button"
                      onClick={() => setManufacturerPrefix(active ? '' : lower)}
                      className={cn(
                        'px-2 py-1 text-xs rounded-[var(--ds-radius-md)] border min-w-[2rem] transition-colors',
                        active
                          ? 'bg-[var(--ds-accent-primary)] text-white border-transparent'
                          : 'border-[var(--ds-border-subtle)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]'
                      )}
                    >
                      {L}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {listQuery.status === 'loading' && !listQuery.data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-[var(--ds-text-muted)]" />
        </div>
      ) : listQuery.isError ? (
        <Card>
          <CardContent className="p-8 text-center text-[var(--ds-status-danger)]">
            {listQuery.error?.message || 'Could not load documents.'}
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-[var(--ds-text-muted)]">
            No documents match your filters.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="overflow-x-auto rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead className="whitespace-nowrap">Size</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead className="whitespace-nowrap text-center">Versions</TableHead>
                  <TableHead className="whitespace-nowrap">Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="font-medium text-[var(--ds-text-primary)] truncate" title={doc.title}>
                          {doc.title}
                        </p>
                        {doc.description ? (
                          <p className="text-xs text-[var(--ds-text-muted)] line-clamp-2 mt-0.5">
                            {doc.description}
                          </p>
                        ) : null}
                        <p className="text-micro text-[var(--ds-text-muted)] mt-1 truncate">
                          {doc.originalFileName}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[var(--ds-text-secondary)]">
                      {kbCategoryLabel(doc.category)}
                    </TableCell>
                    <TableCell className="text-sm align-top">
                      <div className="flex flex-wrap gap-1 max-w-[12rem]">
                        {(doc.tags || []).length === 0 ? (
                          <span className="text-[var(--ds-text-muted)]">—</span>
                        ) : (
                          (doc.tags || []).map((t) => (
                            <Badge key={t.slug} variant="neutral" className="font-normal text-micro px-1.5 py-0">
                              {t.label || t.slug}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[var(--ds-text-secondary)]">
                      {doc.manufacturer || '—'}
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{formatBytes(doc.byteSize)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <Badge variant={doc.visibility === 'public' ? 'success' : 'neutral'}>
                          {doc.visibility === 'public' ? 'Public' : 'Private'}
                        </Badge>
                        {doc.ownerTenantId === tenantId && (
                          <span className="text-micro text-[var(--ds-text-muted)]">Your upload</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm tabular-nums text-center">
                      {doc.versionCount != null ? doc.versionCount : '—'}
                    </TableCell>
                    <TableCell className="text-sm text-[var(--ds-text-secondary)] whitespace-nowrap">
                      {doc.createdAt ? format(new Date(doc.createdAt), 'dd MMM yyyy') : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1 flex-wrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 px-2"
                          disabled={downloadBusyId === doc.id}
                          onClick={() => onDownload(doc)}
                          title="Download current file"
                        >
                          {downloadBusyId === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 px-2"
                          onClick={() => onVersionHistory(doc)}
                          title="Version history"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        {canModifyDoc(doc) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => onReplaceFile(doc)}
                            title="Upload a new file version"
                          >
                            <FileUp className="h-4 w-4" />
                          </Button>
                        )}
                        {canModifyDoc(doc) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => onEditTags(doc)}
                            title="Edit tags"
                          >
                            <Tag className="h-4 w-4" />
                          </Button>
                        )}
                        {canModifyDoc(doc) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-[var(--ds-status-danger)] border-[var(--ds-status-danger)]/40 hover:bg-[var(--ds-status-danger-bg)]"
                            disabled={deleteBusyId === doc.id}
                            onClick={() => onDelete(doc)}
                            title="Remove from knowledge base"
                          >
                            {deleteBusyId === doc.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[var(--ds-text-secondary)]">
            <p>
              Showing {items.length} of {total} document{total === 1 ? '' : 's'}
              {listQuery.isFetching ? ' · Updating…' : ''}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-meta tabular-nums">
                Page {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function UploadPanel({ onUploaded, onError }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general_manuals');
  const [manufacturer, setManufacturer] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [uploadTags, setUploadTags] = useState([]);
  const [uploadTagInput, setUploadTagInput] = useState('');
  const [uploadTagSuggestions, setUploadTagSuggestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);

  const manufacturerRequired = KB_MANUFACTURER_REQUIRED_CATEGORIES.has(category);

  useEffect(() => {
    const q = manufacturer.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      knowledgeBaseAPI
        .manufacturerSuggestions({ q })
        .then((res) => setSuggestions(res?.data?.suggestions ?? []))
        .catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [manufacturer]);

  useEffect(() => {
    const q = uploadTagInput.trim();
    if (q.length < 1) {
      setUploadTagSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      knowledgeBaseAPI
        .tagSuggestions({ q, limit: 20 })
        .then((res) => setUploadTagSuggestions(res?.data?.items ?? []))
        .catch(() => setUploadTagSuggestions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [uploadTagInput]);

  const addUploadTag = (raw) => {
    const slug = normalizeTagSlugClient(raw);
    if (!slug || uploadTags.includes(slug)) return;
    if (uploadTags.length >= 30) {
      onError('You can add at most 30 tags per document.');
      return;
    }
    setUploadTags([...uploadTags, slug]);
    setUploadTagInput('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      onError('Choose a file to upload.');
      return;
    }
    if (!title.trim()) {
      onError('Title is required.');
      return;
    }
    if (manufacturerRequired && !manufacturer.trim()) {
      onError('Manufacturer is required for this category.');
      return;
    }
    if (!acceptDisclaimer) {
      onError('Please confirm you accept the upload disclaimer.');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('description', description);
    fd.append('category', category);
    fd.append('visibility', visibility);
    if (manufacturer.trim()) fd.append('manufacturer', manufacturer.trim());
    fd.append('acceptDisclaimer', 'true');
    if (uploadTags.length) fd.append('tagSlugs', JSON.stringify(uploadTags));

    setUploading(true);
    try {
      await knowledgeBaseAPI.uploadDocument(fd);
      setFile(null);
      setTitle('');
      setDescription('');
      setManufacturer('');
      setUploadTags([]);
      setUploadTagInput('');
      setAcceptDisclaimer(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploaded();
    } catch (err) {
      onError(err?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div className="space-y-6">
      <Card className="border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/20">
        <CardContent className="p-4 sm:p-5">{UPLOAD_DISCLAIMER}</CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={onSubmit} className="space-y-5 max-w-xl">
            <div
              className={cn(
                'border-2 border-dashed rounded-[var(--ds-radius-md)] p-8 text-center transition-colors',
                'border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)]/50',
                'hover:border-[var(--ds-accent-primary)]/50'
              )}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <Upload className="h-10 w-10 mx-auto text-[var(--ds-text-muted)] mb-3" />
              <p className="text-sm text-[var(--ds-text-secondary)] mb-2">
                Drag a file here, or{' '}
                <button
                  type="button"
                  className="text-[var(--ds-accent-primary)] font-medium underline-offset-2 hover:underline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  browse
                </button>
              </p>
              <p className="text-micro text-[var(--ds-text-muted)]">
                PDF, images, Word, Excel, plain text or CSV (max size set by your server).
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.docx,.xlsx,.txt,.csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {file && (
                <p className="mt-4 text-sm font-medium text-[var(--ds-text-primary)]">{file.name}</p>
              )}
            </div>

            <div>
              <FormLabel htmlFor="kb-title">Title</FormLabel>
              <Input
                id="kb-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={500}
                placeholder="Short description of the document"
              />
            </div>

            <div>
              <FormLabel htmlFor="kb-desc">Description (optional)</FormLabel>
              <Textarea
                id="kb-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={10000}
                placeholder="Extra context for search"
                className="w-full min-h-[4rem] rounded-[var(--ds-radius-md)] border border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] px-3 py-2 text-sm"
              />
            </div>

            <Select
              label="Category"
              requiredIndicator
              required
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {KB_CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>

            <div>
              <FormLabel>Tags (optional)</FormLabel>
              <p className="text-micro text-[var(--ds-text-muted)] mb-2">
                Lowercase labels; new tags are created automatically. Press Enter to add.
              </p>
              <div className="flex flex-wrap gap-1.5 mb-2 min-h-[1.5rem]">
                {uploadTags.map((slug) => (
                  <span
                    key={slug}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)]"
                  >
                    {slug}
                    <button
                      type="button"
                      className="p-0.5 rounded-full hover:bg-[var(--ds-control-field-bg)]"
                      onClick={() => setUploadTags(uploadTags.filter((s) => s !== slug))}
                      aria-label={`Remove ${slug}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <Input
                id="kb-upload-tags"
                value={uploadTagInput}
                onChange={(e) => setUploadTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addUploadTag(uploadTagInput);
                  }
                }}
                placeholder="e.g. bs7671, plc, training"
                list="kb-upload-tag-suggestions"
              />
              <datalist id="kb-upload-tag-suggestions">
                {uploadTagSuggestions.map((item) => (
                  <option key={item.slug} value={item.slug}>
                    {item.label || item.slug}
                  </option>
                ))}
              </datalist>
            </div>

            <div>
              <FormLabel htmlFor="kb-mfr">
                Manufacturer {manufacturerRequired ? <span className="text-[var(--ds-status-danger)]">*</span> : null}
              </FormLabel>
              <Input
                id="kb-mfr"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                placeholder={manufacturerRequired ? 'Required for this category' : 'Optional'}
                list="kb-mfr-suggestions"
              />
              <datalist id="kb-mfr-suggestions">
                {suggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </div>

            <fieldset>
              <legend className="text-meta font-medium text-[var(--ds-text-secondary)] mb-2">Visibility</legend>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="kb-vis"
                    checked={visibility === 'public'}
                    onChange={() => setVisibility('public')}
                  />
                  Public (all SparxMate businesses)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="kb-vis"
                    checked={visibility === 'private'}
                    onChange={() => setVisibility('private')}
                  />
                  Private (my business only)
                </label>
              </div>
            </fieldset>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 rounded border-[var(--ds-control-field-border)]"
                checked={acceptDisclaimer}
                onChange={(e) => setAcceptDisclaimer(e.target.checked)}
              />
              <span className="text-sm text-[var(--ds-text-secondary)]">
                I confirm this upload complies with the disclaimer above and I am allowed to share this
                material.
              </span>
            </label>

            <Button type="submit" disabled={uploading} className="gap-2">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload document
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function EditDocumentTagsModal({ doc, onClose, onSaved, onError }) {
  const [slugs, setSlugs] = useState(() => (doc?.tags || []).map((t) => t.slug));
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSlugs((doc?.tags || []).map((t) => t.slug));
  }, [doc?.id]);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 1) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      knowledgeBaseAPI
        .tagSuggestions({ q, limit: 20 })
        .then((res) => setSuggestions(res?.data?.items ?? []))
        .catch(() => setSuggestions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [input]);

  const add = (raw) => {
    const s = normalizeTagSlugClient(raw);
    if (!s || slugs.includes(s)) return;
    if (slugs.length >= 30) {
      onError('At most 30 tags per document.');
      return;
    }
    setSlugs([...slugs, s]);
    setInput('');
  };

  const save = async () => {
    if (!doc?.id) return;
    setSaving(true);
    try {
      await knowledgeBaseAPI.updateDocument(doc.id, { tagSlugs: slugs });
      onSaved();
    } catch (e) {
      onError(e?.response?.data?.error || e?.message || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="kb-edit-tags-title"
        className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] max-w-md w-full p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 id="kb-edit-tags-title" className="text-lg font-semibold text-[var(--ds-text-primary)]">
              Edit tags
            </h2>
            <p className="text-sm text-[var(--ds-text-secondary)] mt-1 line-clamp-2">{doc?.title}</p>
          </div>
          <button
            type="button"
            className="p-1 rounded-md text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-bg)]"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-micro text-[var(--ds-text-muted)] mb-2">
          Press Enter to add a tag. Removing all tags clears labels for this document.
        </p>
        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[2rem]">
          {slugs.map((slug) => (
            <span
              key={slug}
              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-[var(--ds-accent-primary)]/12 border border-[var(--ds-border-subtle)]"
            >
              {slug}
              <button
                type="button"
                className="p-0.5 rounded-full hover:bg-[var(--ds-surface-panel)]"
                onClick={() => setSlugs(slugs.filter((s) => s !== slug))}
                aria-label={`Remove ${slug}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(input);
            }
          }}
          placeholder="Add tag…"
          list="kb-modal-tag-suggestions"
          className="mb-4"
        />
        <datalist id="kb-modal-tag-suggestions">
          {suggestions.map((item) => (
            <option key={item.slug} value={item.slug}>
              {item.label || item.slug}
            </option>
          ))}
        </datalist>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => save()} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save tags
          </Button>
        </div>
      </div>
    </div>
  );
}

function VersionHistoryModal({ doc, onClose, onError, downloadBusyKey, setDownloadBusyKey }) {
  const versionsQuery = useQuery({
    queryKey: ['knowledge-base', 'versions', doc?.id],
    queryFn: () => knowledgeBaseAPI.listDocumentVersions(doc.id),
    enabled: !!doc?.id,
  });

  const items = versionsQuery.data?.data?.items ?? [];

  const downloadVersion = async (v) => {
    const key = `${doc.id}:${v.id}`;
    setDownloadBusyKey(key);
    try {
      const res = await knowledgeBaseAPI.downloadDocumentVersion(doc.id, v.id);
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = v.originalFileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(e?.response?.data?.error || e?.message || 'Could not download');
    } finally {
      setDownloadBusyKey(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="kb-versions-title"
        className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[var(--ds-border-subtle)]">
          <div>
            <h2 id="kb-versions-title" className="text-lg font-semibold text-[var(--ds-text-primary)]">
              Version history
            </h2>
            <p className="text-sm text-[var(--ds-text-secondary)] mt-1 line-clamp-2">{doc?.title}</p>
          </div>
          <button
            type="button"
            className="p-1 rounded-md text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-bg)]"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {versionsQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--ds-text-muted)]" />
            </div>
          ) : versionsQuery.isError ? (
            <p className="text-sm text-[var(--ds-status-danger)]">
              {versionsQuery.error?.message || 'Could not load versions.'}
            </p>
          ) : items.length === 0 ? (
            <p className="text-sm text-[var(--ds-text-muted)]">No versions found.</p>
          ) : (
            <ul className="space-y-3">
              {items.map((v) => {
                const busy = downloadBusyKey === `${doc.id}:${v.id}`;
                return (
                  <li
                    key={v.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-3"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-[var(--ds-text-primary)] tabular-nums">
                          v{v.versionNumber}
                        </span>
                        {v.isCurrent ? (
                          <Badge variant="success" className="text-micro">
                            Current
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-micro text-[var(--ds-text-muted)] truncate mt-0.5">
                        {v.originalFileName} · {formatBytes(v.byteSize)}
                      </p>
                      <p className="text-micro text-[var(--ds-text-secondary)] mt-1">
                        {v.createdAt ? format(new Date(v.createdAt), 'dd MMM yyyy HH:mm') : '—'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0 gap-2"
                      disabled={busy}
                      onClick={() => downloadVersion(v)}
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Download
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ReplaceFileModal({ doc, onClose, onDone, onError }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(() => doc?.title || '');
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTitle(doc?.title || '');
    setFile(null);
    setAcceptDisclaimer(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [doc?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      onError('Choose a replacement file.');
      return;
    }
    const t = title.trim();
    if (!t) {
      onError('Title is required.');
      return;
    }
    if (!acceptDisclaimer) {
      onError('Please confirm you accept the upload disclaimer.');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('acceptDisclaimer', 'true');
      fd.append('title', t);
      await knowledgeBaseAPI.updateDocument(doc.id, fd);
      onDone();
    } catch (err) {
      onError(err?.response?.data?.error || err?.message || 'Could not replace file.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="kb-replace-title"
        className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] max-w-md w-full p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 id="kb-replace-title" className="text-lg font-semibold text-[var(--ds-text-primary)]">
              Replace file
            </h2>
            <p className="text-sm text-[var(--ds-text-secondary)] mt-1">
              Upload a new file. Earlier files stay in version history.
            </p>
          </div>
          <button
            type="button"
            className="p-1 rounded-md text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-bg)]"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="rounded-[var(--ds-radius-md)] border border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/20 p-3 mb-4 text-sm text-[var(--ds-text-secondary)]">
          Same rules as new uploads: only permitted, non-sensitive reference material.
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <FormLabel>File</FormLabel>
            <input
              ref={fileInputRef}
              type="file"
              className="block w-full text-sm text-[var(--ds-text-secondary)] mt-1"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.docx,.xlsx,.txt,.csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div>
            <FormLabel htmlFor="kb-replace-title-input">Title</FormLabel>
            <Input
              id="kb-replace-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={500}
              required
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 rounded border-[var(--ds-control-field-border)]"
              checked={acceptDisclaimer}
              onChange={(e) => setAcceptDisclaimer(e.target.checked)}
            />
            <span className="text-sm text-[var(--ds-text-secondary)]">
              I confirm this file complies with the knowledge base policy and I am allowed to share it.
            </span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              Replace file
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
